import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  Alert,
  Platform 
} from 'react-native';
import { 
  XStack, 
  YStack, 
  H2, 
  Progress, 
  Button,
  Sheet
} from 'tamagui';
import { 
  MaterialIcons, 
  Feather, 
  AntDesign,
  MaterialCommunityIcons 
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import CompactOverview from './CompactOverview';
import { useRoute } from '@react-navigation/native';

interface ModernProjectHeaderProps {
  navigation?: any;
}

const ModernProjectHeader: React.FC<ModernProjectHeaderProps> = ({
  navigation
}) => {
  const theme = themeSelector();
  const route = useRoute<any>();
  const [searchVisible, setSearchVisible] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);

  // Extract all data from route params with memoization
  const routeParams = useMemo(() => route.params || {}, [route.params]);
  const { 
    project, 
    projectData, 
    headerProps 
  } = routeParams;

  const currentProject = useMemo(() => project || projectData, [project, projectData]);
  const tasks = useMemo(() => headerProps?.tasks || [], [headerProps?.tasks]);
  const selectedTasks = useMemo(() => headerProps?.selectedTasks || [], [headerProps?.selectedTasks]);
  
  // Memoize callback functions to prevent recreation
  const onSearch = useCallback((query: string) => {
    headerProps?.onSearch?.(query);
  }, [headerProps?.onSearch]);
  
  const onFilter = useCallback((filter: string) => {
    headerProps?.onFilter?.(filter);
  }, [headerProps?.onFilter]);
  
  const onBulkAction = useCallback((action: string) => {
    headerProps?.onBulkAction?.(action);
  }, [headerProps?.onBulkAction]);
  
  const onSettings = useCallback(() => {
    headerProps?.onSettings?.();
  }, [headerProps?.onSettings]);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      setSearchVisible(false);
      setFilterVisible(false);
      setBulkActionsVisible(false);
    };
  }, []);

  console.log("ModernProjectHeader Route Params:", {
    project: currentProject?.title,
    tasksLength: tasks.length,
    selectedTasksLength: selectedTasks.length,
    hasHeaderProps: !!headerProps
  });

  // Memoized utility functions to prevent recreation
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'completed': return '#3b82f6';
      case 'on-hold': return '#f59e0b';
      case 'planning': return '#8b5cf6';
      default: return '#6b7280';
    }
  }, []);

  const getPriorityColor = useCallback((priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('Date formatting error:', error);
      return dateString;
    }
  }, []);

  const calculateProgress = useCallback(() => {
    if (!currentProject?.workPackages?.length) return 0;
    try {
      const totalTasks = currentProject.workPackages.reduce((acc, wp) => acc + (wp.tasks?.length || 0), 0);
      const completedTasks = currentProject.workPackages.reduce((acc, wp) => 
        acc + (wp.tasks?.filter(task => task.status === 'done').length || 0), 0);
      return totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    } catch (error) {
      console.warn('Progress calculation error:', error);
      return 0;
    }
  }, [currentProject?.workPackages]);

  const progress = useMemo(() => calculateProgress(), [calculateProgress]);

  return (
    <View style={{
      backgroundColor: theme ? '#ffffff' : '#111827',
      paddingTop: Platform.OS === 'ios' ? 44 : 16,
      paddingBottom: 12,
      paddingHorizontal: 16,
      borderBottomWidth: 1,
      borderBottomColor: theme ? '#e5e7eb' : '#374151',
    }}>
      {/* Compact Header Row */}
      <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
        {/* Back Button + Title */}
        <XStack alignItems="center" flex={1}>
          <TouchableOpacity
            onPress={() => navigation?.navigate('ProjectsList')}
            style={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
              marginRight: 12
            }}
          >
            <AntDesign 
              name="arrow-left" 
              size={18} 
              color={theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>
          
          <View style={{ flex: 1 }}>
            <Text style={{
              userSelect: 'none',
              fontSize: 18,
              fontWeight: '700',
              color: theme ? '#111827' : '#ffffff',
            }} numberOfLines={1}>
              {currentProject?.title || 'Project'}
            </Text>
          </View>
        </XStack>

        {/* Settings Button */}
        <TouchableOpacity
          onPress={onSettings}
          style={{
            padding: 6,
            borderRadius: 6,
            backgroundColor: theme ? '#f3f4f6' : '#374151',
          }}
        >
          <MaterialIcons 
            name="more-vert" 
            size={20} 
            color={theme ? '#111827' : '#ffffff'} 
          />
        </TouchableOpacity>
      </XStack>

      {/* Compact Info Row */}
      <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
        {/* Status + Priority + Team */}
        <XStack alignItems="center" gap="$2" flex={1}>
          <View style={{
            backgroundColor: getStatusColor(currentProject?.status || 'planning'),
            paddingHorizontal: 8,
            paddingVertical: 2,
            borderRadius: 8,
          }}>
            <Text style={{
              userSelect: 'none',
              color: '#ffffff',
              fontWeight: '600',
              fontSize: 10,
            }}>
              {currentProject?.status?.replace('-', ' ').toUpperCase() || 'PLANNING'}
            </Text>
          </View>

          {currentProject?.priority && (
            <View style={{
              backgroundColor: getPriorityColor(currentProject.priority),
              paddingHorizontal: 8,
              paddingVertical: 2,
              borderRadius: 8,
            }}>
              <Text style={{
                userSelect: 'none',
                color: '#ffffff',
                fontWeight: '600',
                fontSize: 10,
              }}>
                {currentProject.priority.toUpperCase()}
              </Text>
            </View>
          )}

          {currentProject?.team?.length > 0 && (
            <XStack alignItems="center" gap="$1">
              <MaterialIcons 
                name="group" 
                size={14} 
                color={theme ? '#6b7280' : '#9ca3af'} 
              />
              <Text style={{
                userSelect: 'none',
                fontSize: 12,
                color: theme ? '#6b7280' : '#9ca3af',
                fontWeight: '500'
              }}>
                {currentProject.team.length}
              </Text>
            </XStack>
          )}
        </XStack>

        {/* Compact Progress */}
        <XStack alignItems="center" gap="$2">
          <Text style={{
            userSelect: 'none',
            fontSize: 14,
            color: theme ? '#111827' : '#ffffff',
            fontWeight: '600'
          }}>
            {Math.round(progress)}%
          </Text>
          <Progress 
            value={progress} 
            size="$2" 
            width={60}
            backgroundColor={theme ? '#e5e7eb' : '#374151'}
          >
            <Progress.Indicator 
              backgroundColor={progress >= 100 ? '#10b981' : '#3b82f6'} 
            />
          </Progress>
        </XStack>
      </XStack>

      {/* Compact Action Row */}
      <XStack alignItems="center" justifyContent="space-between">
        {/* Date Range */}
        <XStack alignItems="center" gap="$1" flex={1}>
          {(currentProject?.startDate || currentProject?.endDate) && (
            <>
              <MaterialIcons 
                name="date-range" 
                size={14} 
                color={theme ? '#6b7280' : '#9ca3af'} 
              />
              <Text style={{
                userSelect: 'none',
                fontSize: 12,
                color: theme ? '#6b7280' : '#9ca3af'
              }}>
                {currentProject.startDate && formatDate(currentProject.startDate)}
                {currentProject.startDate && currentProject.endDate && ' - '}
                {currentProject.endDate && formatDate(currentProject.endDate)}
              </Text>
            </>
          )}
        </XStack>

        {/* Action Buttons */}
        <XStack gap="$1">
          <TouchableOpacity
            onPress={() => setSearchVisible(!searchVisible)}
            style={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: searchVisible 
                ? '#3b82f6' 
                : theme ? '#f3f4f6' : '#374151',
            }}
          >
            <Feather 
              name="search" 
              size={16} 
              color={searchVisible ? '#ffffff' : theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => setFilterVisible(!filterVisible)}
            style={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: filterVisible 
                ? '#3b82f6' 
                : theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name="filter-list" 
              size={16} 
              color={filterVisible ? '#ffffff' : theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>

          {selectedTasks.length > 0 && (
            <TouchableOpacity
              onPress={() => setBulkActionsVisible(!bulkActionsVisible)}
              style={{
                padding: 6,
                paddingHorizontal: 8,
                borderRadius: 6,
                backgroundColor: '#f59e0b',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Text style={{
                userSelect: 'none', color: '#ffffff', fontSize: 12, fontWeight: '600'
              }}>
                {selectedTasks.length}
              </Text>
              <MaterialIcons name="check-box" size={14} color="#ffffff" />
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialCommunityIcons 
              name="export" 
              size={16} 
              color={theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              padding: 6,
              borderRadius: 6,
              backgroundColor: theme ? '#f3f4f6' : '#374151',
            }}
          >
            <MaterialIcons 
              name="share" 
              size={16} 
              color={theme ? '#111827' : '#ffffff'} 
            />
          </TouchableOpacity>
        </XStack>
      </XStack>

      {/* Search Bar */}
      {searchVisible && (
        <Animatable.View 
          animation="slideInDown" 
          duration={300}
          style={{
            marginTop: 16,
            padding: 12,
            backgroundColor: theme ? '#f9fafb' : '#374151',
            borderRadius: 12,
            borderWidth: 1,
            borderColor: theme ? '#e5e7eb' : '#4b5563',
          }}
        >
          <TextInput
            placeholder="Search tasks, work packages..."
            placeholderTextColor={theme ? '#6b7280' : '#9ca3af'}
            style={{
              fontSize: 16,
              color: theme ? '#111827' : '#ffffff',
              padding: 0,
            }}
            onChangeText={onSearch}
            autoFocus
          />
        </Animatable.View>
      )}

      {/* Filter Options */}
      {filterVisible && (
        <Animatable.View 
          animation="slideInDown" 
          duration={300}
          style={{
            marginTop: 16,
            flexDirection: 'row',
            gap: 8,
          }}
        >
          {['all', 'active', 'completed'].map(filter => (
            <TouchableOpacity
              key={filter}
              onPress={() => onFilter(filter)}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 20,
                backgroundColor: theme ? '#f3f4f6' : '#374151',
                borderWidth: 1,
                borderColor: theme ? '#e5e7eb' : '#4b5563',
              }}
            >
              <Text style={{
                userSelect: 'none',
                fontSize: 14,
                fontWeight: '500',
                color: theme ? '#111827' : '#ffffff',
                textTransform: 'capitalize'
              }}>
                {filter}
              </Text>
            </TouchableOpacity>
          ))}
        </Animatable.View>
      )}

      {/* Bulk Actions */}
      {bulkActionsVisible && selectedTasks.length > 0 && (
        <Animatable.View 
          animation="slideInDown" 
          duration={300}
          style={{
            marginTop: 16,
            flexDirection: 'row',
            gap: 8,
          }}
        >
          <TouchableOpacity
            onPress={() => onBulkAction('assign')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: '#3b82f6',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4
            }}
          >
            <MaterialIcons name="person-add" size={16} color="#ffffff" />
            <Text style={{
              userSelect: 'none', color: '#ffffff', fontSize: 14, fontWeight: '500'
            }}>
              Assign
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => onBulkAction('archive')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: '#f59e0b',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4
            }}
          >
            <MaterialIcons name="archive" size={16} color="#ffffff" />
            <Text style={{
              userSelect: 'none', color: '#ffffff', fontSize: 14, fontWeight: '500'
            }}>
              Archive
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => onBulkAction('delete')}
            style={{
              paddingHorizontal: 16,
              paddingVertical: 8,
              borderRadius: 8,
              backgroundColor: '#ef4444',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4
            }}
          >
            <MaterialIcons name="delete" size={16} color="#ffffff" />
            <Text style={{
              userSelect: 'none', color: '#ffffff', fontSize: 14, fontWeight: '500'
            }}>
              Delete
            </Text>
          </TouchableOpacity>
        </Animatable.View>
      )}

      {/* Compact Overview Section */}
      {/* <CompactOverview project={currentProject} tasks={tasks || []} /> */}
    </View>
  );
};

export default ModernProjectHeader;
